<script lang="ts">
	import qs from 'qs';
	import { format } from 'svelte-i18n';
	import { api, repo } from '../services/http';
	//Design system components
	import { ButtonV2, Text, InputV3, toast, AlertV2 } from '@Shelly/shelly-design-system';
	import { capFirstChar } from 'src/helpers/utilities';
	import { applicationHostUrl } from 'src/helpers/account_helpers';
	import { replace } from 'svelte-spa-router';

	import { validate_email } from 'src/helpers/validators';
	import { beforeUpdate } from 'svelte';

	let email,
		delete_request_success = false,
		show_alert = false,
		loading = false;

	let alertProps = {
			heading: '',
			message: '',
		},
		pageTitle = '',
		pageSubtitle = '';

	let mailError: boolean | string = false;

	// handle inputs verification before sending to the request function
	const validate = () => {
		const isValidState = {
			email: true,
		};

		if (email === '') {
			mailError = $format('label_enter_email');
			isValidState.email = false;
		} else if (!validate_email(email)) {
			mailError = $format('input_error_valid_email', {
				default: 'Please enter a valid email!',
			});
			isValidState.email = false;
		}

		return isValidState.email;
	};

	const clearErrors = () => {
		if (show_alert) {
			show_alert = false;
			alertProps = {
				heading: '',
				message: '',
			};
		}
		if (mailError) {
			mailError = false;
		}
	};

	const handleRequestDeleteAccount = async (email) => {
		if (validate()) {
			clearErrors();
			loading = true;
			await requestDeleteAccount(email).then((res) => {
				loading = false;

				if (res && !res.error) {
					delete_request_success = true;

					pageTitle = getPageTitle();
					pageSubtitle = getPageSubtitle();
				} else {
					if (res.error === 'not_existing') {
						alertProps.heading = $format('error_wrong_email', {
							default: `Error, ${res.error}`,
						});
						alertProps.message = $format('no_account_err_toast', {
							default: 'There is no account with this email.',
						});
					} else if (res.error === 'mail_sent') {
						alertProps.heading = $format('alert_mail_sent', {
							default: `Error, ${res.error}`,
						});
						alertProps.message = $format('alert_error_mail_sent', {
							default: 'Email already sent in last hour.',
						});
					}

					console.warn('Error on deleting account request', res.error, alertProps);
					delete_request_success = false;
					show_alert = true;
				}
			});
		}
	};

	const requestDeleteAccount = async (email) => {
		//this will set control.shelly.cloud for clients which are using app.
		const url = applicationHostUrl(
			import.meta.env.VITE_APP_BUCKET || window.location.host,
			'/#/account-delete',
		);
		try {
			const res = await api.post(
				'/auth/remove_account',
				qs.stringify({
					email,
					url,
				}),
				{
					headers: {
						'Content-Type': 'application/x-www-form-urlencoded',
					},
				},
			);
			toast.open({
				type: 'info',
				title: $format('toast_delete_req_sent', {
					values: { value: email },
					default:
						'Account deletion request sent. If this account is registered you will receive further instructions there.',
				}),
			});
			return { isok: res };
		} catch (error) {
			const errorMessageKey = Object.keys(error?.response?.data?.errors)?.[0];
			return { error: errorMessageKey };
		}
	};

	const onInputEmail = (event: Event) => {
		const target = event.target as HTMLInputElement;
		email = target.value;
	};

	const backToLogin = () => {
		delete_request_success = false;
		replace('/login');
	};

	const getPageTitle = () => {
		if (delete_request_success) {
			return $format('title_delete_account_req_sent', {
				default: 'Delete account request was sent',
			});
		}
		return $format('title_delete_account', {
			default: 'Delete account',
		});
	};

	const getPageSubtitle = () => {
		if (delete_request_success) {
			return $format('text_forgotten_pass_check_email', {
				default: 'Please check your email.',
			});
		}
		return $format('text_forgotten_pass_enter_email', {
			default: 'Enter the email you used for your account.',
		});
	};

	beforeUpdate(() => {
		pageTitle = getPageTitle();
		pageSubtitle = getPageSubtitle();
	});
</script>

<Text element="h1" class="mb-m pl-s">
	{capFirstChar(pageTitle)}
</Text>

<Text class="text-center mb-xl ">
	{pageSubtitle}
</Text>
{#if show_alert}
	<AlertV2
		type="danger"
		class="mb-m"
		heading={alertProps.heading}
		message={alertProps.message}
		isHeadingBold
		on:close={() => clearErrors()}
	/>
{/if}

{#if delete_request_success}
	<Text class="text-center mb-m">
		{$format('text_delete_account_success', {
			default: 'Account deletion request was sent successfully.',
		})}
	</Text>
{/if}
{#if !delete_request_success}
	<InputV3
		id="input-request-delete-account"
		aria-labelledby="input-request-delete-account"
		bind:value={email}
		size="l"
		class="mb-m"
		label={capFirstChar($format('placeholder_email', { default: 'e-mail' }))}
		icon={'fa-regular fa-envelope'}
		type="text"
		inputmode={'email'}
		error={mailError ? `${mailError}` : ''}
		on:click={() => clearErrors()}
		on:enter={() => handleRequestDeleteAccount(email)}
		on:change={onInputEmail}
		disabled={loading}
	/>

	<ButtonV2
		class="mb-m"
		variant="danger"
		width="full"
		{loading}
		disabled={email === '' || loading}
		on:click={() => handleRequestDeleteAccount(email)}
	>
		{capFirstChar($format('button_delete_account', { default: 'delete account' }))}
	</ButtonV2>

	<ButtonV2 width="full" variant="secondary" disabled={loading} on:click={backToLogin}>
		{capFirstChar($format('button_back', { default: 'Back' }))}
	</ButtonV2>
{:else}
	<ButtonV2 width="full" variant="primary" disabled={loading} on:click={backToLogin}>
		{capFirstChar($format('link_back_to_login', { default: 'Back to login' }))}
	</ButtonV2>
{/if}
